<template>
  <modal :open="internalOpen" :width="1400">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <form-card
        :title="$t('pages.mountings.mounting.mountingRegistrations.modal.driver.title')"
        :description="
          $t('pages.mountings.mounting.mountingRegistrations.modal.driver.description')
        "
        variant="singleColumn"
      >
        <div class="flex">
          <Readonly
            class="mb-3 w-1/4"
            :label="$t('user.firstName')"
          >
            {{ mountingTimeslotRegistration?.driver?.user?.firstName ?? '' }}
          </Readonly>
          <Readonly
            class="mb-3 w-1/4"
            :label="$t('user.lastName')"
          >
            {{ mountingTimeslotRegistration?.driver?.user?.lastName ?? '' }}
          </Readonly>
          <Readonly
            class="mb-3 w-1/4"
            :label="$t('user.email')"
          >
            {{ mountingTimeslotRegistration?.driver?.user?.email ?? '' }}
          </Readonly>
          <Readonly
            class="mb-3 w-1/4"
            :label="$t('user.phoneNumber')"
          >
            {{ mountingTimeslotRegistration?.driver?.user?.phoneNumber ?? '' }}
          </Readonly>
        </div>
        <div class="flex">
          <Readonly
            v-if="mountingTimeslotRegistration?.campaignRegistration"
            class="mb-3 w-1/2"
            :label="$t('mountingTimeslotRegistration.campaign')"
          >
            {{ mountingTimeslotRegistration?.campaignRegistration?.campaign?.name ?? '' }}
          </Readonly>
          <Readonly
            v-else
            class="mb-3 w-1/2"
            :label="$t('mountingTimeslotRegistration.license')"
          >
            {{ formatLicense(mountingTimeslotRegistration?.timeslot?.mounting?.license) }}
          </Readonly>
          <Readonly
            class="mb-3 w-1/2"
            :label="$t('mountingTimeslotRegistration.type.DEFAULT')"
          >
            {{ $t(`mountingTimeslotRegistration.type.${mountingTimeslotRegistration?.type}`) }}
          </Readonly>
        </div>
        <div class="flex flex-col gap-y-5 my-5">
          <text-area
            :model-value="mountingTimeslotRegistration?.adminComment ?? ''"
            :label="$t('mountingTimeslotRegistration.adminComment')"
            class="w-full"
            disabled
          />
        </div>
      </form-card>

      <form-card
        :title="$t('pages.mountings.mounting.mountingRegistrations.modal.oldBox.title')"
        :description="
          $t('pages.mountings.mounting.mountingRegistrations.modal.oldBox.description')
        "
        variant="singleColumn"
      >
        <div v-if="oldBox" class="flex">
          <Readonly
            class="mb-3 w-1/4"
            :label="$t('box.seal')"
          >
            {{ oldBox?.sealNumber ?? '' }}
          </Readonly>
          <Readonly
            class="mb-3 w-1/4"
            :label="$t('box.type')"
          >
            {{ oldBox?.type ?? '' }}
          </Readonly>
          <Readonly
            class="mb-3 w-1/4"
            :label="$t('box.variant.DEFAULT')"
          >
            {{ oldBox?.variant ? $t(`box.variant.${oldBox?.variant}`) : '' }}
          </Readonly>
        </div>
        <div v-else>
          <p class="text-neutral">
            {{ $t('pages.mountings.mounting.mountingRegistrations.modal.oldBox.noOldBox') }}
          </p>
        </div>
        <text-area
          v-if="mountingTimeslotRegistration.type === 'unmounting'"
          v-model="rawData.comment"
          :error="errors?.comment"
          :label="$t('mountingTimeslotRegistration.comment')"
          class="w-full mt-5"
        />
      </form-card>
      <form-card
        v-if="mountingTimeslotRegistration.type !== 'unmounting'"
        :title="$t('pages.mountings.mounting.mountingRegistrations.modal.box.title')"
        :description="
          $t('pages.mountings.mounting.mountingRegistrations.modal.box.description')
        "
        variant="singleColumn"
      >
        <div class="flex flex-col gap-y-5">
          <div class="flex gap-x-5">
            <FormInput
              v-model="rawData.box.sealNumber"
              :error="errors?.sealNumber"
              type="text"
              class="w-1/4"
              :label="$t('box.seal')"
            />

            <Multiselect
              v-model="rawData.box.type"
              :error="errors?.type"
              :options="boxTypes"
              :label="$t('box.type')"
              :can-deselect="false"
              :can-clear="false"
              class="w-1/4"
            />
            <Multiselect
              v-model="rawData.box.variant"
              :error="errors?.box.variant"
              :options="boxVariants"
              :label="$t('box.variant.DEFAULT')"
              :can-deselect="false"
              :can-clear="false"
              class="w-1/4"
            />
            <div class="w-1/4">
              <DatePicker
                v-model="rawData.box.mountingDate"
                :error="errors?.mountingDate"
                mode="datetime"
                :label="$t('box.mountingDate')"
                class="w-full"
              />
            </div>
          </div>
          <div class="flex gap-x-5">
            <FormInput
              v-model="rawData.box.keyNumber"
              :error="errors?.box?.keyNumber"
              type="text"
              class="w-1/4"
              :label="$t('box.keyNumber')"
            />
            <Multiselect
              v-model="rawData.box.stable"
              :error="errors?.box?.stable"
              class="w-1/4"
              :label="$t('box.stable')"
              :options="[
                {
                  value: 'true',
                  label: $t('yes'),
                },
                {
                  value: 'false',
                  label: $t('no'),
                }
              ]"
              :can-deselect="false"
              :can-clear="false"
            />
            <Multiselect
              v-model="rawData.box.boxBaseSurface"
              :error="errors?.box?.boxBaseSurface"
              class="w-1/4"
              :label="$t('box.boxBaseSurface.DEFAULT')"
              :options="boxBaseSurfaceOptions"
              :can-clear="false"
              :can-deselect="false"
            />
            <div class="w-1/4" />
          </div>
          <!--          <Multiselect-->
          <!--            v-model="rawData.box.status"-->
          <!--            :error="errors?.status"-->
          <!--            :options="enumOptionsProvider('/enums/BoxStatus')"-->
          <!--            :label="$t('box.statusTitle')"-->
          <!--            :can-deselect="false"-->
          <!--            class="w-1/2"-->
          <!--            :can-clear="false"-->
          <!--          />-->

          <!--          <Multiselect-->
          <!--            v-model="rawData.box.firstCampaign"-->
          <!--            :error="errors?.firstCampaign"-->
          <!--            :label="$t('box.campaign')"-->
          <!--            :options="fetchCampaigns"-->
          <!--            :can-deselect="false"-->
          <!--            :can-clear="false"-->
          <!--          />-->
          <!--          <FormInput-->
          <!--            v-model="rawData.box.malusOnFirstCampaignPayment"-->
          <!--            :error="errors?.malusOnFirstCampaignPayment"-->
          <!--            type="number"-->
          <!--            :label="$t('box.malusOnFirstCampaignPayment')"-->
          <!--          />-->
        </div>
      </form-card>
      <form-card
        :title="$t('pages.mountings.mounting.mountingRegistrations.modal.additionalStuff.title')"
        :description="
          $t('pages.mountings.mounting.mountingRegistrations.modal.additionalStuff.description')
        "
        variant="singleColumn"
      >
        <div class="flex flex-col gap-y-5">
          <div class="flex gap-x-5">
            <FormInput
              v-model="rawData.phoneModel"
              :error="errors?.phoneModel"
              type="text"
              class="w-1/3"
              :label="$t('mountingTimeslotRegistration.phoneModel')"
            />
            <Multiselect
              v-model="rawData.returnType"
              :error="errors?.returnType"
              class="w-1/3"
              :label="$t('mountingTimeslotRegistration.returnType.DEFAULT')"
              :options="returnTypeOptions"
              :can-deselect="true"
              :can-clear="true"
            />
          </div>

          <text-area
            v-model="rawData.comment"
            :error="errors?.comment"
            :label="$t('mountingTimeslotRegistration.comment')"
            class="w-full"
          />
        </div>
      </form-card>
      <div class="justify-center flex gap-x-5">
        <custom-button
          variant="primary"
          class="mt-15"
          :busy="loadingSave"
          @click="save"
        >
          {{
            mountingTimeslotRegistration.type === 'unmounting'
              ? $t('pages.mountings.mounting.mountingRegistrations.modal.completeUnmountingButton') : $t('save')
          }}
        </custom-button>
        <custom-button
          v-if="isAdmin"
          variant="outlined"
          class="mt-15 !border-red !text-red"
          :busy="loadingSaveAndComplete"
          @click="saveAndComplete"
        >
          {{
            mountingTimeslotRegistration.type === 'unmounting'
              ? $t('pages.mountings.mounting.mountingRegistrations.modal.completeUnmountingButtonAndApprove') : $t('saveAndApprove')
          }}
        </custom-button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { date, object, string } from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import FormInput from '@utils/inputs/Input.vue';
import Multiselect from '@components/utils/Multiselect.vue';
import DatePicker from '@components/utils/inputs/DatePicker.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import { enumOptionsProvider } from '@utils/table/hooks/provider';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import { syncRef } from '@hooks/state';
import CustomButton from '@utils/CustomButton.vue';
import TextArea from '@utils/inputs/TextArea.vue';
import Readonly from '@utils/Readonly.vue';
import { format } from 'date-fns';
import type { Box, License, MountingTimeslotRegistration } from '@/entities/Entities';
import type { ByReference } from '@/utils/types';
import { useStore } from '@/store';

interface Props {
  open: boolean;
  mountingTimeslotRegistration?: MountingTimeslotRegistration;
}

const emit = defineEmits(['update:open', 'update:mountingTimeslotRegistration', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalMountingTimeslotRegistration = useVModel(props, 'mountingTimeslotRegistration', emit);

const store = useStore();
const isAdmin = store.getters['auth/IS_ADMIN'];

const isNew = computed(
  () => !props.mountingTimeslotRegistration?.driver?.box,
);
const tKey = computed(() =>
  (isNew.value
    ? 'pages.mountings.mounting.mountingRegistrations.modal.new'
    : 'pages.mountings.mounting.mountingRegistrations.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const returnTypeOptions = enumOptionsProvider('/enums/MountingTimeslotRegistrationReturnType', (item) => ({
  value: item,
  label: t(`mountingTimeslotRegistration.returnType.${item}`),
}));

const oldBox = computed(() => {
  const savedOldBox = props.mountingTimeslotRegistration?.oldBox;

  if (savedOldBox) {
    return savedOldBox;
  }

  const driverBox = props.mountingTimeslotRegistration?.driver?.box;
  const newBox = props.mountingTimeslotRegistration?.box;
  if (driverBox && newBox?.id !== driverBox.id) {
    return driverBox;
  }
  return undefined;
});
const boxBaseSurfaceOptions = enumOptionsProvider('/enums/BoxBaseSurface', (item) => ({
  value: item,
  label: t(`box.boxBaseSurface.${item}`),
}));

const initialBox: Partial<ByReference<Box, 'firstCampaign'>> = {
  mountingDate: undefined,
  type: undefined,
  variant: undefined,
  sealNumber: undefined,
  stable: undefined,
  keyNumber: '',
  boxBaseSurface: undefined,
};

const initialData = (mountingTimeslotRegistration?: MountingTimeslotRegistration) =>
  (mountingTimeslotRegistration
    ? {
      ...mountingTimeslotRegistration,
      box: mountingTimeslotRegistration?.box
        ? {
          ...mountingTimeslotRegistration.box,
          sealNumber:
                mountingTimeslotRegistration.box.sealNumber ?? '',
          firstCampaign:
                mountingTimeslotRegistration.box.firstCampaign?.['@id'],
          mountingDate: mountingTimeslotRegistration.box
            .mountingDate
            ? new Date(
              mountingTimeslotRegistration.box.mountingDate,
            )
            : new Date(),
        }
        : mountingTimeslotRegistration?.type === 'unmounting'
          ? null
          : {
          mountingDate: mountingTimeslotRegistration?.timeslot?.startTime
            ? new Date(mountingTimeslotRegistration.timeslot.startTime)
            : undefined,
          type: 'v2',
          variant: undefined,
          sealNumber: undefined,
          stable: undefined,
          keyNumber: '',
          boxBaseSurface: undefined,
        },
    }
    : {
      phoneModel: '',
      returnType: undefined,
      comment: undefined,
      box: initialBox,
    });

const rawData = syncRef(internalMountingTimeslotRegistration, (mountingTimeslotRegistration) => ({
  ...initialData(mountingTimeslotRegistration),
}));

const boxTypes = enumOptionsProvider('/enums/BoxType');
const boxVariants = enumOptionsProvider('/enums/BoxVariant', (item) => ({
  value: item,
  label: t(`box.variant.${item}`),
}));

const {
  data, errors, validatedFn, setValidation,
} = useValidation(
  object({
    comment: string().nullable(),
    phoneModel: string(),
    returnType: string().nullable(),
    box: object({
      mountingDate: date().nullable().required(t('validation.notEmpty')),
      type: string().nullable().required(t('validation.notEmpty')),
      variant: string().nullable().required(t('validation.notEmpty')),
      sealNumber: string().nullable(),
      stable: string().nullable().required(t('validation.notEmpty')),
      keyNumber: string(),
      boxBaseSurface: string().nullable().required(t('validation.notEmpty')),
    }).nullable(),
  }),
  rawData,
);

function resetData() {
  rawData.value = { ...initialData() };
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec] = useFetchAction();

const request = validatedFn(async (andComplete = false) => {
  try {
    await exec(`${props.mountingTimeslotRegistration?.['@id']}/complete`, {
      method: 'PUT',
      body: {
        box: props.mountingTimeslotRegistration?.type !== 'unmounting' ? {
          id: props.mountingTimeslotRegistration?.box?.['@id'],
          sealNumber: data.value.box.sealNumber,
          mountingDate: data.value.box.mountingDate,
          type: data.value.box.type,
          variant: data.value.box.variant,
          keyNumber: data.value.box.keyNumber,
          stable: data.value.box.stable === 'true',
          boxBaseSurface: data.value.box.boxBaseSurface ?? null,
        } : null,
        comment: data.value.comment,
        phoneModel: data.value.phoneModel,
        returnType: data.value.returnType ?? null,
        andComplete,
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.mountings.mounting.mountingRegistrations.modal.edit.saveSuccess'),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.mountings.mounting.mountingRegistrations.modal.edit.saveError'),
    });
  }
});

const loadingSave = ref(false);
const loadingSaveAndComplete = ref(false);

const save = async () => {
  loadingSave.value = true;
  await request(false);
  loadingSave.value = false;
};
const saveAndComplete = async () => {
  loadingSaveAndComplete.value = true;
  await request(isAdmin);
  loadingSaveAndComplete.value = false;
};

const formatLicense = (license: License | undefined) => {
  if (!license) return '-';
  return `${license.company?.name} - ${license.startDate ? format(new Date(license.startDate), 'dd.MM.yyyy') : ''} - ${license.endDate ? format(new Date(license.endDate), 'dd.MM.yyyy') : ''}`;
};
</script>

<style lang="scss" scoped></style>
